import { useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '../utils/queryKeys'

import HubbleHeader from '@/components/layouts/HubbleHeader'
import NavigationSidebar from '@/components/layouts/NavigationSidebar'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import type {
  AdminApplicationItem,
  AdminClientItem,
} from '@/types/controlCenter'
import { cn } from '@/utils/cn'

import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal'
import RowActions from '../components/shared/RowActions'
import SortIcon, { type SortDir } from '../components/shared/SortIcon'
import StatusMessage from '../components/shared/StatusMessage'
import TablePagination from '../components/shared/TablePagination'
import { thCls, thSortBtnCls, tdCls } from '../components/shared/tableStyles'
import { addBtnCls, navInputCls } from '../components/shared/modalStyles'
import useAdminApplications from '../hooks/useAdminApplications'
import useAdminClients from '../hooks/useAdminClients'
import { fmtDateTime } from '../utils/format'
import ApplicationModal from './ApplicationModal'
import ClientModal from './ClientModal'

type ConfigTab = 'applications' | 'clients'
type AppSortField = 'id' | 'name' | 'created_at' | 'key'

const PAGE_SIZE = 10

const tabButtonCls = (active: boolean) =>
  cn(
    'px-4 py-1.5 text-sm font-medium rounded-md transition',
    active && 'bg-neutral-dimmed-heavy text-text-primary',
    !active && 'text-text-tertiary hover:text-text-secondary',
  )

function sortItems<T>(items: T[], field: keyof T, dir: SortDir): T[] {
  return [...items].sort((a, b) => {
    const cmp = String(a[field] ?? '').localeCompare(
      String(b[field] ?? ''),
      undefined,
      { numeric: true },
    )
    return dir === 'asc' ? cmp : -cmp
  })
}

interface AppTableProps {
  items: AdminApplicationItem[]
  search: string
  isCreating: boolean
  onCreateClose: () => void
}

const ApplicationsTable = ({
  items,
  search,
  isCreating,
  onCreateClose,
}: AppTableProps) => {
  const queryClient = useQueryClient()
  const [sortField, setSortField] = useState<AppSortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(0)
  const [editingApp, setEditingApp] = useState<AdminApplicationItem | null>(
    null,
  )
  const [deletingApp, setDeletingApp] = useState<AdminApplicationItem | null>(
    null,
  )

  useEffect(() => {
    setPage(0)
  }, [search])

  const deleteMutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.applications,
      })
      setDeletingApp(null)
    },
  })

  const toggleSort = (field: AppSortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
  }

  const q = search.toLowerCase()
  const filtered = sortItems(
    items.filter(
      (a) =>
        !q ||
        a.name.toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q),
    ),
    sortField,
    sortDir,
  )
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <>
      <div className='border-border-main bg-neutral-dimmed-heavy flex flex-1 flex-col overflow-hidden rounded-lg border'>
        <div className='flex-1 overflow-auto'>
          <Table>
            <TableHeader className='border-border-main bg-neutral-dimmed sticky top-0 border-b'>
              <TableRow className='border-border-main hover:bg-transparent'>
                {(['id', 'name', 'created_at', 'key'] as const).map((field) => (
                  <TableHead
                    key={field}
                    className={thCls}
                    aria-sort={
                      sortField === field
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button
                      type='button'
                      className={thSortBtnCls}
                      onClick={() => toggleSort(field)}
                    >
                      {field === 'id'
                        ? 'ID'
                        : field === 'name'
                          ? 'Application'
                          : field === 'created_at'
                            ? 'Created'
                            : 'Key'}
                      <SortIcon
                        field={field}
                        sortField={sortField}
                        sortDir={sortDir}
                      />
                    </button>
                  </TableHead>
                ))}
                <TableHead className={thCls}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className='divide-border-main divide-y'>
              {paged.map((app) => (
                <TableRow
                  key={app.id}
                  className='border-border-main hover:bg-neutral-dimmed transition'
                >
                  <TableCell
                    className={cn(tdCls, 'text-text-dimmed font-mono')}
                  >
                    {app.id}
                  </TableCell>
                  <TableCell className={cn(tdCls, 'overflow-hidden')}>
                    <span className='text-text-primary block font-semibold break-all'>
                      {app.name}
                    </span>
                    {app.description && (
                      <span className='text-text-tertiary mt-0.5 block text-xs break-all'>
                        {app.description}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      tdCls,
                      'text-text-tertiary whitespace-nowrap',
                    )}
                  >
                    {fmtDateTime(app.created_at)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      tdCls,
                      'text-text-tertiary font-mono text-xs',
                    )}
                  >
                    {app.key || '—'}
                  </TableCell>
                  <TableCell className='px-4 py-3.5'>
                    <RowActions
                      itemName={app.name}
                      onEdit={() => setEditingApp(app)}
                      onDelete={() => setDeletingApp(app)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {paged.length === 0 && (
                <TableRow className='hover:bg-transparent'>
                  <TableCell
                    colSpan={5}
                    className='text-text-tertiary py-12 text-center text-sm'
                  >
                    No applications match your search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        )}
      </div>

      {(isCreating || editingApp) && (
        <ApplicationModal
          app={editingApp ?? undefined}
          onClose={editingApp ? () => setEditingApp(null) : onCreateClose}
        />
      )}
      {deletingApp && (
        <ConfirmDeleteModal
          title='Delete Application'
          itemName={deletingApp.name}
          isPending={deleteMutation.isPending}
          error={deleteMutation.error}
          onCancel={() => setDeletingApp(null)}
          onConfirm={() =>
            deleteMutation.mutate({
              method: 'delete',
              url: `${CONTROL_CENTRE_API_DOMAIN}/admin/applications/${deletingApp.id}`,
            })
          }
        />
      )}
    </>
  )
}

type ClientSortField = 'id' | 'name' | 'created_at'

interface ClientTableProps {
  items: AdminClientItem[]
  search: string
  isCreating: boolean
  onCreateClose: () => void
}

const ClientsTable = ({
  items,
  search,
  isCreating,
  onCreateClose,
}: ClientTableProps) => {
  const queryClient = useQueryClient()
  const [sortField, setSortField] = useState<ClientSortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(0)
  const [editingClient, setEditingClient] = useState<AdminClientItem | null>(
    null,
  )
  const [deletingClient, setDeletingClient] = useState<AdminClientItem | null>(
    null,
  )

  useEffect(() => {
    setPage(0)
  }, [search])

  const deleteMutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.clients,
      })
      setDeletingClient(null)
    },
  })

  const toggleSort = (field: ClientSortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
  }

  const q = search.toLowerCase()
  const filtered = sortItems(
    items.filter(
      (c) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    ),
    sortField,
    sortDir,
  )
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <>
      <div className='border-border-main bg-neutral-dimmed-heavy flex flex-1 flex-col overflow-hidden rounded-lg border'>
        <div className='flex-1 overflow-auto'>
          <Table>
            <TableHeader className='border-border-main bg-neutral-dimmed sticky top-0 border-b'>
              <TableRow className='border-border-main hover:bg-transparent'>
                {(['id', 'name', 'created_at'] as const).map((field) => (
                  <TableHead
                    key={field}
                    className={thCls}
                    aria-sort={
                      sortField === field
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button
                      type='button'
                      className={thSortBtnCls}
                      onClick={() => toggleSort(field)}
                    >
                      {field === 'id'
                        ? 'ID'
                        : field === 'name'
                          ? 'Client'
                          : 'Created'}
                      <SortIcon
                        field={field}
                        sortField={sortField}
                        sortDir={sortDir}
                      />
                    </button>
                  </TableHead>
                ))}
                <TableHead className={thCls}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className='divide-border-main divide-y'>
              {paged.map((client) => (
                <TableRow
                  key={client.id}
                  className='border-border-main hover:bg-neutral-dimmed transition'
                >
                  <TableCell
                    className={cn(tdCls, 'text-text-dimmed font-mono')}
                  >
                    {client.id}
                  </TableCell>
                  <TableCell className={cn(tdCls, 'overflow-hidden')}>
                    <span className='text-text-primary block font-semibold break-all'>
                      {client.name}
                    </span>
                    {client.description && (
                      <span className='text-text-tertiary mt-0.5 block text-xs break-all'>
                        {client.description}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      tdCls,
                      'text-text-tertiary whitespace-nowrap',
                    )}
                  >
                    {fmtDateTime(client.created_at)}
                  </TableCell>
                  <TableCell className='px-4 py-3.5'>
                    <RowActions
                      itemName={client.name}
                      onEdit={() => setEditingClient(client)}
                      onDelete={() => setDeletingClient(client)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {paged.length === 0 && (
                <TableRow className='hover:bg-transparent'>
                  <TableCell
                    colSpan={4}
                    className='text-text-tertiary py-12 text-center text-sm'
                  >
                    No clients match your search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        )}
      </div>

      {(isCreating || editingClient) && (
        <ClientModal
          client={editingClient ?? undefined}
          onClose={editingClient ? () => setEditingClient(null) : onCreateClose}
        />
      )}
      {deletingClient && (
        <ConfirmDeleteModal
          title='Delete Client'
          itemName={deletingClient.name}
          isPending={deleteMutation.isPending}
          error={deleteMutation.error}
          onCancel={() => setDeletingClient(null)}
          onConfirm={() =>
            deleteMutation.mutate({
              method: 'delete',
              url: `${CONTROL_CENTRE_API_DOMAIN}/admin/clients/${deletingClient.id}`,
            })
          }
        />
      )}
    </>
  )
}

const Configuration = () => {
  const [tab, setTab] = useState<ConfigTab>('applications')
  const [search, setSearch] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const {
    applications,
    isLoading: appsLoading,
    error: appsError,
  } = useAdminApplications()

  const {
    clients,
    isLoading: clientsLoading,
    error: clientsError,
  } = useAdminClients()

  const isApps = tab === 'applications'
  const loading = isApps ? appsLoading : clientsLoading
  const error = isApps ? appsError : clientsError

  const switchTab = (next: ConfigTab) => {
    setTab(next)
    setSearch('')
    setIsCreating(false)
  }

  return (
    <div className='flex w-full flex-1 flex-col'>
      <SidebarProvider>
        <HubbleHeader />
        <NavigationSidebar className='md:top-[3rem] md:h-[calc(100vh-3rem)]' />

        <SidebarInset>
          <div className='relative mt-[3rem] flex h-[calc(100vh-3rem)] w-full flex-1 flex-col text-white'>
            <header className='border-border-shadow bg-neutral flex h-12 items-center border-b px-1 md:justify-between'>
              <SidebarTrigger className='z-40 my-1 hidden md:inline-flex' />
              <h1 className='mx-3 inline-flex space-x-1 text-sm font-medium'>
                <span className='text-text-tertiary'>Control Center</span>
                <span className='text-divider-main'>/</span>
                <span className='text-text-primary'>
                  {isApps ? 'Applications' : 'Clients'}
                </span>
              </h1>
              <div className='flex-1' />
            </header>

            <nav className='border-divider-main bg-neutral-dimmed flex min-h-12 flex-wrap items-center gap-3 border-b px-4 py-2'>
              <div className='border-border-main bg-neutral flex items-center gap-1 rounded-lg border p-1'>
                <button
                  className={tabButtonCls(isApps)}
                  onClick={() => switchTab('applications')}
                >
                  Applications
                </button>
                <button
                  className={tabButtonCls(!isApps)}
                  onClick={() => switchTab('clients')}
                >
                  Clients
                </button>
              </div>
              <div className='flex-1' />
              <input
                type='text'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search…'
                className={cn(navInputCls, 'w-[180px]')}
              />
              <button onClick={() => setIsCreating(true)} className={addBtnCls}>
                + {isApps ? 'Add Application' : 'Add Client'}
              </button>
            </nav>

            <div className='bg-neutral-dimmed flex min-h-0 flex-1 overflow-auto px-6 py-4'>
              {loading ? (
                <StatusMessage loading />
              ) : error ? (
                <StatusMessage
                  text={`Failed to load ${isApps ? 'applications' : 'clients'}.`}
                  error
                />
              ) : isApps ? (
                <ApplicationsTable
                  items={applications}
                  search={search}
                  isCreating={isCreating}
                  onCreateClose={() => setIsCreating(false)}
                />
              ) : (
                <ClientsTable
                  items={clients}
                  search={search}
                  isCreating={isCreating}
                  onCreateClose={() => setIsCreating(false)}
                />
              )}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}

export default Configuration
